<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { VclList } from "vue-content-loading";
import { getCachedPages, loadPages } from "@/state/helpers/pages.helper";

export default {
  locales: {
    pt: {
      'Home': 'Início',
      'How it works': 'Como Funciona',
      'Content': 'Conteúdo',
      'Presentation': 'Apresentação',
      'Updated on': 'Atualizado em',
      'Previous': 'Anterior',
      'Next': 'Próxima',
      'Content coming soon.': 'Conteúdo em breve.',
      'We apologize, we are unable to retrieve information at this time. Please try again later.': 'Pedimos desculpas, não podemos recuperar informações no momento. Por favor, tente novamente mais tarde.',
      'Try again': 'Tentar novamente',
    },
    es: {
      'Home': 'Inicio',
      'How it works': 'Cómo Funciona',
      'Content': 'Contenido',
      'Presentation': 'Presentación',
      'Updated on': 'Actualizado el',
      'Previous': 'Anterior',
      'Next': 'Siguiente',
      'Content coming soon.': 'Contenido próximamente.',
      'We apologize, we are unable to retrieve information at this time. Please try again later.': 'Nos disculpamos, no podemos recuperar información en este momento. Por favor, inténtelo de nuevo más tarde.',
      'Try again': 'Intentar de nuevo',
    }
  },
  components: {
    Layout,
    Breadcrumb,
    VclList
  },
  data() {
    return {
      // Começa com a lista salva no localStorage (sem esperar a API)
      pages: getCachedPages(),
      page: null,
      loading: true,
      errored: false,
      empty: false,
      presentationUrl: 'https://docs.google.com/gview?url=https://s3.m2n.com.br/elomobiliarios/about/consultor-club.pdf&embedded=true'
    };
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    currentIndex() {
      return this.pages.findIndex(item => item.slug === this.slug);
    },
    previousPage() {
      return this.currentIndex > 0 ? this.pages[this.currentIndex - 1] : null;
    },
    nextPage() {
      if (this.currentIndex < 0) return null;
      return this.currentIndex < this.pages.length - 1 ? this.pages[this.currentIndex + 1] : null;
    },
    pageTitle() {
      if (this.page && this.page.title) return this.page.title;
      const item = this.pages[this.currentIndex];
      return item ? item.title : this.t('How it works');
    },
    selectOptions() {
      return this.pages.map(item => ({ value: item.slug, text: item.title }));
    },
    updatedLabel() {
      if (!this.page || !this.page.updated) return '';
      const parts = String(this.page.updated).substring(0, 10).split('-');
      return parts.length === 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : '';
    }
  },
  methods: {
    // Lista de páginas: consulta a API uma vez por entrada no app e salva no localStorage
    getPages() {
      loadPages().then(list => {
        this.pages = list;
      });
    },
    getPage() {
      this.loading = true;
      this.errored = false;
      this.empty = false;
      this.page = null;

      api
        .get('pages/' + encodeURIComponent(this.slug))
        .then(response => {
          if (response.data.status == 'success') {
            this.page = response.data.page;
            if (!this.page.content || this.page.content.trim() === '') {
              this.empty = true;
            }
          } else {
            this.empty = true;
          }
        })
        .catch(() => {
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reload() {
      if (this.pages.length === 0) {
        this.getPages();
      }
      this.getPage();
    },
    goTo(slug) {
      if (slug && slug !== this.slug) {
        this.$router.push('/comofunciona/' + slug);
      }
    }
  },
  watch: {
    '$route.params.slug'() {
      this.getPage();
      window.scrollTo(0, 0);
    }
  },
  mounted() {
    this.getPages();
    this.getPage();
  }
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: t('How it works') }, { text: pageTitle }]" />
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-md-flex justify-content-between">
          <div>
            <h3 class="mb-0">{{ pageTitle }}</h3>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div v-if="pages.length > 0" class="col-12 d-lg-none mb-3">
        <b-form-select :value="slug" :options="selectOptions" @change="goTo"></b-form-select>
      </div>

      <div class="col-lg-3 d-none d-lg-block">
        <div class="card hw-nav">
          <div class="card-body">
            <h5 class="card-title mb-3">{{ t('How it works') }}</h5>
            <vcl-list v-if="pages.length === 0 && loading"></vcl-list>
            <ul class="list-unstyled mb-0">
              <li v-for="item in pages" :key="item.slug">
                <router-link :to="'/comofunciona/' + item.slug" class="hw-nav-link" :class="{ active: item.slug === slug }">
                  {{ item.title }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-lg-9">
        <div class="card">
          <div class="card-body">
            <vcl-list v-if="loading"></vcl-list>
            <div v-else-if="errored" class="text-center py-4">
              <p>{{ t('We apologize, we are unable to retrieve information at this time. Please try again later.') }}</p>
              <button type="button" class="btn btn-primary" @click="reload">{{ t('Try again') }}</button>
            </div>
            <div v-else-if="empty" class="text-center text-muted py-5">
              {{ t('Content coming soon.') }}
            </div>
            <div v-else>
              <div class="hw-content" v-html="page.content"></div>
              <p v-if="updatedLabel" class="text-muted small mt-4 mb-0">{{ t('Updated on') }} {{ updatedLabel }}</p>
            </div>

            <div v-if="!loading && (previousPage || nextPage)" class="d-flex justify-content-between mt-4">
              <div>
                <router-link v-if="previousPage" :to="'/comofunciona/' + previousPage.slug" class="btn btn-light">
                  &lsaquo; {{ t('Previous') }}
                </router-link>
              </div>
              <div>
                <router-link v-if="nextPage" :to="'/comofunciona/' + nextPage.slug" class="btn btn-light">
                  {{ t('Next') }} &rsaquo;
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <div v-if="slug === 'como-participar'" class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">{{ t('Presentation') }}</h5>
            <iframe :src="presentationUrl" style="width:100%; height:600px;" frameborder="0"></iframe>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.hw-nav {
  position: sticky;
  top: 90px;
}
.hw-nav-link {
  display: block;
  padding: 6px 10px;
  border-radius: 4px;
  color: inherit;
}
.hw-nav-link:hover,
.hw-nav-link.active {
  background: rgba(1, 113, 235, 0.08);
  color: #0171eb;
  font-weight: 600;
}
.hw-content {
  line-height: 1.7;
  overflow-wrap: break-word;
}
.hw-content >>> h2 {
  font-size: 1.4rem;
  margin: 1.5rem 0 0.75rem;
}
.hw-content >>> h3 {
  font-size: 1.15rem;
  margin: 1.25rem 0 0.5rem;
}
.hw-content >>> p {
  margin-bottom: 1rem;
}
.hw-content >>> ul,
.hw-content >>> ol {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}
.hw-content >>> table {
  display: block;
  width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin-bottom: 1rem;
}
.hw-content >>> table td,
.hw-content >>> table th {
  border: 1px solid #dee2e6;
  padding: 6px 10px;
}
.hw-content >>> img {
  max-width: 100%;
  height: auto;
}
.hw-content >>> a {
  color: #0171eb;
  text-decoration: underline;
}
</style>
